import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NewbornPostpartumSizeQuestionsLinkIds } from "@/encounters/constants/newborn-postpartum";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

const SizeDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornPostpartumSize,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousSizeAnswers = getQuestionnaireAnswers(previousVersion);
  const currentSizeAnswers = getQuestionnaireAnswers(currentVersion);

  const previousScale =
    previousSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Scale]?.valueString;
  const currentScale = currentSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Scale]?.valueString;
  const previousWeight =
    previousSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Weight]?.valueDecimal;
  const currentWeight =
    currentSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Weight]?.valueDecimal;
  const previousWeightOunces =
    previousSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.WeightOunces]?.valueDecimal;
  const currentWeightOunces =
    currentSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.WeightOunces]?.valueDecimal;
  const previousLength =
    previousSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Length]?.valueDecimal;
  const currentLength =
    currentSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Length]?.valueDecimal;
  const previousHeadCircumference =
    previousSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.HeadCircumference]?.valueDecimal;
  const currentHeadCircumference =
    currentSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.HeadCircumference]?.valueDecimal;
  const previousChestCircumference =
    previousSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.ChestCircumference]?.valueDecimal;
  const currentChestCircumference =
    currentSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.ChestCircumference]?.valueDecimal;
  const previousNotes =
    previousSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Notes]?.valueString;
  const currentNotes = currentSizeAnswers[NewbornPostpartumSizeQuestionsLinkIds.Notes]?.valueString;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Size Measurements</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Scale</TextWrapper>
                <WordsDiff showRemoved current={currentScale} previous={previousScale} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Weight</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentWeight?.toString()}
                  previous={previousWeight?.toString()}
                />
              </Stack>
              {(previousWeightOunces !== undefined || currentWeightOunces !== undefined) && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Weight (oz)</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentWeightOunces?.toString()}
                    previous={previousWeightOunces?.toString()}
                  />
                </Stack>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Length</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentLength?.toString()}
                  previous={previousLength?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Head Circumference</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentHeadCircumference?.toString()}
                  previous={previousHeadCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Chest Circumference</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentChestCircumference?.toString()}
                  previous={previousChestCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Scale</TextWrapper>
                <WordsDiff current={currentScale} previous={previousScale} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Weight</TextWrapper>
                <WordsDiff
                  current={currentWeight?.toString()}
                  previous={previousWeight?.toString()}
                />
              </Stack>
              {(previousWeightOunces !== undefined || currentWeightOunces !== undefined) && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Weight (oz)</TextWrapper>
                  <WordsDiff
                    current={currentWeightOunces?.toString()}
                    previous={previousWeightOunces?.toString()}
                  />
                </Stack>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Length</TextWrapper>
                <WordsDiff
                  current={currentLength?.toString()}
                  previous={previousLength?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Head Circumference</TextWrapper>
                <WordsDiff
                  current={currentHeadCircumference?.toString()}
                  previous={previousHeadCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Chest Circumference</TextWrapper>
                <WordsDiff
                  current={currentChestCircumference?.toString()}
                  previous={previousChestCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default SizeDiff;
